import { Fragment, useCallback, useMemo } from "react";
import {
  Box, Collapse, Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography
} from "@mui/material";
import { DateTime } from "luxon";
import { CabCheckbox } from "@CabComponents/CabCheckbox";
import { Leader, Calendar, WeekStartDay } from "../../../store";
import chroma from "chroma-js";
import colors from "../../../colors";
import { CabIcon } from "@CabComponents/CabIcon";
import {
  IoAddOutline, IoChevronDownOutline, IoChevronUpOutline, IoEyeOffOutline, IoEyeOutline, IoWarning,
} from "react-icons/io5";
import { BsPin, BsPinAngleFill } from "react-icons/bs";
import { useState } from "react";
import AdditionalCalendarsModal from "../../../components/Schedule/AdditionalCalendarsModal";
import { View as RBCView, Views as RBCViews } from 'react-big-calendar';
import { getWeekRange } from "../../../utils/scheduleUtils";
import { CabTooltip } from "@CabComponents/CabTooltip";
import UserPrefCabDatePicker from "@CabComponents/CabDatePicker/UserPrefDatePicker";
import { useNavigate } from "react-router-dom";
import { PAGE_URL } from "../../../constants";
import { CalendarAccessIndicators } from "../../../components/CalendarAccessIndicators";


const sidePanelWidth = '275px';

interface SidePanelProps {
  open: boolean;
  onCancel: () => void;
  leaders: Leader[];
  selectedLeaders: number[];
  onLeaderSelect: (selection: { leaderId: number, selected: boolean }[]) => void;
  middleOfInterval: DateTime;
  onUpdateDateRange: (start: DateTime, end: DateTime) => void;
  hiddenCalendarIds: number[];
  onToggleCalendarVisibility: (payload: { calendarId: number, hidden: boolean }[]) => void;
  leaderIdCalendarMap: Record<number, Calendar[]>;
  leaderIdPrimaryCalendarMap: Record<number, Calendar | undefined>;
  additionalCalendars: (
    Calendar & {
      title: string;
      subtitle: string;
      calendar_access_id: number;
      pinnedCalendarId?: number;
      selected: boolean;
    }
  )[];
  calendarView: RBCView;
  allLoaded: boolean;
  onCheckAdditionalCalendar: (payload: { calendarAccessId: number, selected: boolean }[]) => void;
  onTogglePinnedCalendar: (calendarAccessId: number, pinnedCalendarId?: number) => void;
  weekStartDay?: WeekStartDay | null;
  /** Calendar timezone name; used when interpreting mini calendar selection (avoids DST/offset bugs). */
  timezoneName?: string;
}

const SidePanel = ({
  open, onCancel, leaders, selectedLeaders, onLeaderSelect, additionalCalendars, middleOfInterval, onUpdateDateRange,
  hiddenCalendarIds, onToggleCalendarVisibility, leaderIdCalendarMap, leaderIdPrimaryCalendarMap, calendarView,
  allLoaded, onCheckAdditionalCalendar, onTogglePinnedCalendar, weekStartDay, timezoneName = ''
}: SidePanelProps) => {
  const [openAdditionalCalendarsModal, setOpenAdditionalCalendarsModal] = useState(false);
  const [expandedLeaderIds, setExpandedLeaderIds] = useState<Set<number>>(new Set());
  const navigate = useNavigate();

  const hiddenCalendarSet = useMemo(() => new Set(hiddenCalendarIds), [hiddenCalendarIds]);

  // additionally sort leaders first if they have a calendar
  const sortedLeaders = useMemo(() => {
    return leaders.toSorted((a, b) => {
      const aHasCalendar = leaderIdCalendarMap[a.id].length > 0;
      const bHasCalendar = leaderIdCalendarMap[b.id].length > 0;
      if (aHasCalendar && !bHasCalendar) {
        return -1;
      } else if (!aHasCalendar && bHasCalendar) {
        return 1;
      }
      return 0;
    });
  }, [leaders, leaderIdCalendarMap]);

  const handleLeaderSelect = (selection: { leaderId: number, selected: boolean }[]) => {
    onLeaderSelect(selection);
  };

  const handleUpdateLeaderSelection = useCallback((ids: number[]): void => {
    onLeaderSelect(ids.map(id => ({ leaderId: id, selected: true })));
  }, [onLeaderSelect]);

  const handleCheckLeader = (leaderId: number, checked: boolean) => {
    handleLeaderSelect([{ leaderId, selected: checked }]);
  };

  const handleToggleCalendarVisibility = (calendarId: number) => {
    onToggleCalendarVisibility([{ calendarId, hidden: !hiddenCalendarSet.has(calendarId) }]);
  };

  const handleToggleLeaderExpansion = (leaderId: number) => {
    setExpandedLeaderIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leaderId)) newSet.delete(leaderId);
      else newSet.add(leaderId);
      return newSet;
    });
  };

  const viewType = calendarView === RBCViews.WEEK ? 'week'
    : calendarView === RBCViews.WORK_WEEK ? 'work_week'
      : 'day';

  const handleCalendarWarningClick = () => {
    navigate(PAGE_URL.MANAGE_CALENDARS);
  };

  return (
    <Drawer
      open={open}
      onClose={onCancel}
      variant="persistent"
      anchor="left"
      sx={{
        width: open ? sidePanelWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: sidePanelWidth,
          position: 'relative',
          height: '100%',
          overflow: 'hidden',
          opacity: allLoaded ? 1 : 0.7,
          pointerEvents: allLoaded ? 'auto' : 'none',
          // backgroundColor: colors.calmGrey50,
        },
      }}
    >
      <Box sx={{ padding: 1.5 }}>
        <UserPrefCabDatePicker
          static
          noActionBar
          condensed
          size="small"
          type={viewType}
          value={middleOfInterval.toISO()}
          onChange={d => {
            if (d) {
              // Use calendar timezone so week boundaries and DST match the main calendar (fixes DST week bug).
              const dateSelected = DateTime.fromISO(d, {
                zone: timezoneName || undefined,
              });
              if (calendarView === RBCViews.WEEK || calendarView === RBCViews.WORK_WEEK) {
                const { start, end } = getWeekRange(dateSelected, weekStartDay);
                onUpdateDateRange(start, end);
              } else {
                onUpdateDateRange(dateSelected.startOf('day'), dateSelected.endOf('day'));
              }
            }
          }}
        />
      </Box>
      <Divider />
      <Box sx={{ /*backgroundColor: colors.calmGrey50,*/ height: '100%', overflow: 'auto' }}>
        <Box sx={{ padding: 2.5, paddingRight: 0.5, overflow: 'auto' }}>
          <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, color: colors.black700 }}>
            Selected Teammates
          </Typography>
          {sortedLeaders.map(leader => (
            <Fragment key={leader.id}>
              <List dense disablePadding sx={{ paddingRight: 1 }}>
                <ListItem
                  disablePadding
                  secondaryAction={leaderIdCalendarMap[leader.id].length > 0 ? (
                    <Box className="collapsible-icon">
                      <CabIcon
                        Icon={expandedLeaderIds.has(leader.id) ? IoChevronUpOutline : IoChevronDownOutline}
                        onClick={() => handleToggleLeaderExpansion(leader.id)}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <CabTooltip
                        title="No calendars associated with this teammate. Click to manage calendars."
                        wrapWithSpan
                      >
                        <CabIcon 
                          Icon={IoWarning} 
                          color="orange" 
                          sx={{ fontSize: 18, cursor: 'pointer' }} 
                          onClick={handleCalendarWarningClick}
                        />
                      </CabTooltip>
                    </Box>
                  )}
                  sx={{
                    marginTop: 1.25,
                    '& .collapsible-icon': { opacity: expandedLeaderIds.has(leader.id) ? 1 : 0 },
                    '&:hover .collapsible-icon': { opacity: 1 },
                    '& .MuiListItemSecondaryAction-root': { right: 0 },
                    paddingLeft: 0.25,
                  }}

                >
                  <ListItemButton role={undefined} dense disableGutters
                    onClick={(e) => {
                      // Don't fire if click originated from checkbox
                      if (!(e.target as HTMLElement).closest('.MuiCheckbox-root')) {
                        handleToggleLeaderExpansion(leader.id);
                      }
                    }}
                    sx={{ paddingTop: 0, paddingBottom: 0 }}
                  >
                    <ListItemIcon sx={{ minWidth: 22, marginRight: 1 }}>
                      <CabCheckbox
                        checked={selectedLeaders.includes(leader.id)}
                        onChange={(e, checked) => handleCheckLeader(leader.id, checked)}
                        overrides={{ disableRipple: true }}
                        sx={
                          leaderIdPrimaryCalendarMap[leader.id] && {
                            '& .MuiSvgIcon-root': {
                              color: leaderIdPrimaryCalendarMap[leader.id]?.backgroundColor
                            },
                            '&.Mui-checked': {
                              '& .MuiSvgIcon-root': {
                                color: leaderIdPrimaryCalendarMap[leader.id]?.backgroundColor
                              }
                            },
                          }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${leader.first_name} ${leader.last_name}`} 
                      sx={{ margin: 0 }}
                      slotProps={{
                        primary: {
                          sx: { fontWeight: 500 },
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>

              <Collapse in={expandedLeaderIds.has(leader.id)}>
                <List dense disablePadding sx={{ paddingRight: 1 }}>
                  {leaderIdCalendarMap[leader.id].map(calendar => (
                    <ListItem
                      disablePadding
                      secondaryAction={(
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box id="calendar-eye-icon">
                            <CabIcon
                              Icon={hiddenCalendarSet.has(calendar.id) ? IoEyeOffOutline : IoEyeOutline}
                              onClick={() => handleToggleCalendarVisibility(calendar.id)}
                            />
                          </Box>
                        </Box>
                      )}
                      sx={{
                        '& #calendar-eye-icon': { opacity: hiddenCalendarSet.has(calendar.id) ? 1 : 0 },
                        '&:hover #calendar-eye-icon': { opacity: 1 },
                        opacity: hiddenCalendarSet.has(calendar.id) ? 0.5 : 1,
                        paddingLeft: 0.25,
                        '& .MuiListItemSecondaryAction-root': { right: 0 },
                      }}
                      key={calendar.id}
                    >
                      <ListItemButton dense disableGutters
                        sx={{
                          paddingTop: 0.25, paddingBottom: 0.25,
                          paddingRight: '25px !important',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 22, marginRight: 0.5, marginLeft: '6px' }}>
                          <Box sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: calendar.backgroundColor
                              && chroma(calendar.backgroundColor).alpha(0.5).hex(),
                            border: `1px solid ${calendar.backgroundColor}`,
                          }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={(
                            <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, minWidth: 0 }}>
                              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                {calendar.summary}
                              </Box>
                              <Box component="span" sx={{ display: "flex", flexShrink: 0, marginLeft: "auto" }}>
                                <CalendarAccessIndicators calendar={calendar} />
                              </Box>
                            </Box>
                          )}
                          sx={{ margin: 0 }}
                          slotProps={{
                            primary: { 
                              sx: {
                                fontStyle: 'italic',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>

            </Fragment>
          ))}

          <Typography
            variant="h6"
            sx={{ marginBottom: 0.5, marginTop: 2, fontSize: 14, fontWeight: 600, color: colors.black700 }}
          >
            Other Calendars
          </Typography>
          <List dense>
            {additionalCalendars.map(calendar => (
              <ListItem
                key={calendar.id}
                disablePadding
                secondaryAction={(
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CabTooltip
                      title={calendar.pinnedCalendarId != null ? "Unpin calendar" : "Pin calendar"}
                      wrapWithSpan
                    >
                      <Box id="calendar-pin-icon">
                        <CabIcon
                          Icon={calendar.pinnedCalendarId != null ? BsPinAngleFill : BsPin}
                          size={17}
                          sx={{ alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }}
                          onClick={() => onTogglePinnedCalendar(calendar.calendar_access_id, calendar.pinnedCalendarId)}
                        />
                      </Box>
                    </CabTooltip>
                    <CabTooltip
                      title={hiddenCalendarSet.has(calendar.id) ? "Show calendar" : "Hide calendar"}
                      wrapWithSpan
                    >
                      <Box id="calendar-eye-icon">
                        <CabIcon
                          Icon={hiddenCalendarSet.has(calendar.id) ? IoEyeOffOutline : IoEyeOutline}
                          onClick={() => handleToggleCalendarVisibility(calendar.id)}
                        />
                      </Box>
                    </CabTooltip>
                  </Box>
                )}
                sx={{
                  '& #calendar-pin-icon': { opacity: calendar.pinnedCalendarId != null ? 1 : 0 },
                  '&:hover #calendar-pin-icon': { opacity: 1 },
                  '& #calendar-eye-icon': { opacity: hiddenCalendarSet.has(calendar.id) ? 1 : 0 },
                  '&:hover #calendar-eye-icon': { opacity: 1 },
                  opacity: hiddenCalendarSet.has(calendar.id) ? 0.5 : 1,
                  paddingLeft: 0.25,
                  '& .MuiListItemSecondaryAction-root': { right: 0 },
                }}

              >
                <ListItemButton
                  role={undefined}
                  dense
                  disableGutters
                  sx={{ paddingRight: '52px !important' }}
                >
                  <ListItemIcon sx={{ minWidth: 22, marginRight: 1 }}>
                    <CabCheckbox
                      checked={calendar.selected}
                      onChange={(e, checked) => (
                        onCheckAdditionalCalendar([{ calendarAccessId: calendar.calendar_access_id, selected: checked}])
                      )}
                      overrides={{ disableRipple: true }}
                      sx={calendar.backgroundColor ? {
                        '& .MuiSvgIcon-root': {
                          color: calendar.backgroundColor
                        },
                        '&.Mui-checked': {
                          '& .MuiSvgIcon-root': {
                            color: calendar.backgroundColor
                          }
                        },
                      } : {}}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={(
                      <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, minWidth: 0 }}>
                        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          {calendar.title}
                        </Box>
                        <Box component="span" sx={{ display: "flex", flexShrink: 0, marginLeft: "auto" }}>
                          <CalendarAccessIndicators calendar={calendar} />
                        </Box>
                      </Box>
                    )}
                    sx={{ margin: 0 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem disablePadding>
              <ListItemButton role={undefined} dense disableGutters
                onClick={() => setOpenAdditionalCalendarsModal(true)}
              >
                <ListItemIcon sx={{ minWidth: 22, marginRight: 1 }}>
                  <CabIcon Icon={IoAddOutline} />
                </ListItemIcon>
                <ListItemText primary="Add Calendar" sx={{ margin: 0 }} slotProps={{
                  primary: { sx: { fontWeight: 600 } }
                }} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Box>
      {openAdditionalCalendarsModal && (
        <AdditionalCalendarsModal
          isOpen={openAdditionalCalendarsModal} 
          onCancel={() => setOpenAdditionalCalendarsModal(false)}
          selectedLeaders={selectedLeaders}
          onSelectLeaders={handleUpdateLeaderSelection}
        />
      )}
    </Drawer>
  );
};

export default SidePanel;
